import { useMemo } from 'react';
import { Globe } from 'lucide-react';
import { Badge } from '@vnetwork/ui/primitives';
import { DataTable, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { useAesHealth } from '@/api/aes';
import { CopyButton } from './AesScrambler';
import { env } from '@/config/env';
import styles from './Aes.module.css';

// ---- Control-plane API reference --------------------------------------------
//
// This service is managed through the control plane (sig-control) — a
// multi-tenant API the console calls DIRECTLY with the partner JWT. The tenant
// is resolved from the token's org claim, so no tenant/serviceId appears in any
// path. The table below mirrors the control plane's /api/v1 route table; paths
// are relative to API_BASE (which already includes /api/v1).

/** Control-plane base — e.g. https://pangolin-controlplane.…/api/v1 (from APP_CONTROL_API_URL). */
const API_BASE = env.controlApiUrl;

type AuthMode = 'open' | 'jwt';

interface EndpointRow {
  method: string;
  path: string;
  auth: AuthMode;
  purpose: string;
  /** Skip the copy button for non-copyable composite rows. */
  noCopy?: boolean;
}

const AUTH_BADGE: Record<AuthMode, { label: string; tone: 'neutral' | 'info' }> = {
  open: { label: 'Open', tone: 'neutral' },
  jwt: { label: 'JWT', tone: 'info' },
};

const ENDPOINTS: EndpointRow[] = [
  {
    method: 'GET',
    path: '/streams',
    auth: 'jwt',
    purpose: 'List your streams (tenant-scoped from the JWT)',
  },
  {
    method: 'POST',
    path: '/streams',
    auth: 'jwt',
    purpose: 'Create a stream — { app, name, source_url, … } → packaged HLS',
  },
  {
    method: 'GET',
    path: '/streams/{app}/{name}',
    auth: 'jwt',
    purpose: 'Fetch one stream — config + live status',
  },
  {
    method: 'PUT',
    path: '/streams/{app}/{name}',
    auth: 'jwt',
    purpose: 'Update a stream config',
  },
  {
    method: 'DELETE',
    path: '/streams/{app}/{name}',
    auth: 'jwt',
    purpose: 'Delete a stream',
  },
  {
    method: 'POST',
    path: '/streams/{app}/{name}/stop',
    auth: 'jwt',
    purpose: 'Stop ingest for a stream (keeps the config)',
  },
  {
    method: 'POST',
    path: '/provision/probe',
    auth: 'jwt',
    purpose: 'Probe a source URL before provisioning',
  },
  {
    method: 'GET',
    path: '/usage/series',
    auth: 'jwt',
    purpose: 'Usage timeseries (bandwidth / requests) over a window',
  },
  {
    method: 'GET',
    path: '/usage/summary',
    auth: 'jwt',
    purpose: 'Usage totals for a window',
  },
  {
    method: 'GET',
    path: '/sessions',
    auth: 'jwt',
    purpose: 'List active playback sessions',
  },
  {
    method: 'DELETE',
    path: '/sessions/{id}',
    auth: 'jwt',
    purpose: 'Revoke a session — invalidates its key',
  },
  {
    method: 'GET',
    path: '/logs · /logs/errors · /logs/rejects',
    auth: 'jwt',
    purpose: 'Audit-event tail — all events, 5xx errors, or auth rejects',
    noCopy: true,
  },
  {
    method: 'GET',
    path: '/metrics',
    auth: 'jwt',
    purpose: 'Gateway metrics snapshot — the data behind this console',
  },
  {
    method: 'GET',
    path: '/healthz',
    auth: 'open',
    purpose: 'Liveness probe — plain "OK"',
  },
];

/**
 * Read-only API reference: the control-plane endpoint this service is managed
 * through, plus its JWT-scoped /api/v1 route table. Every route is tenant-scoped
 * from the partner JWT — no admin key or tenant id ever appears in a path.
 */
export function AesSettings({ serviceId }: { serviceId?: string }) {
  const health = useAesHealth(serviceId);
  const host = API_BASE.replace('https://', '');

  const columns = useMemo<ColumnDef<EndpointRow, unknown>[]>(
    () => [
      {
        id: 'method',
        header: 'Method',
        size: 110,
        cell: ({ row }) => <span className={styles.eventName}>{row.original.method}</span>,
      },
      {
        id: 'path',
        header: 'Path',
        size: 320,
        cell: ({ row }) => (
          <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-1)', minWidth: 0 }}>
            <span className={styles.eventName} style={{ wordBreak: 'break-all' }}>
              {row.original.path}
            </span>
            {!row.original.noCopy && (
              <CopyButton
                value={`${API_BASE}${row.original.path}`}
                label={`${row.original.path} URL`}
              />
            )}
          </span>
        ),
      },
      {
        id: 'auth',
        header: 'Auth',
        size: 130,
        enableSorting: false,
        cell: ({ row }) => {
          const a = AUTH_BADGE[row.original.auth];
          return <Badge tone={a.tone}>{a.label}</Badge>;
        },
      },
      {
        id: 'purpose',
        header: 'Purpose',
        cell: ({ row }) => <span className={styles.cellMuted}>{row.original.purpose}</span>,
      },
    ],
    [],
  );

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {/* API endpoint card — the control-plane base every route below hangs off. */}
      <div className={styles.zoneCard}>
        <span className={styles.zoneIcon}>
          <Globe size={18} />
        </span>
        <div style={{ display: 'grid', gap: 'var(--vn-space-1)', minWidth: 0 }}>
          <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
            API endpoint
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
            <span className={styles.zoneHost}>{host}</span>
            <CopyButton value={API_BASE} label="API endpoint" />
          </span>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <StatusPill
            tone={health.data?.ok ? 'success' : health.isLoading ? 'muted' : 'danger'}
            loading={health.isLoading}
          >
            {health.data?.ok ? 'Online' : health.isLoading ? 'Checking' : 'Unreachable'}
          </StatusPill>
        </div>
      </div>

      <DataTable columns={columns} data={ENDPOINTS} getRowId={(r) => r.path} dense />

      <p className={styles.note}>
        Every route is called with the partner JWT (Authorization: Bearer) and is scoped to
        your tenant from the token's org claim — no tenant id or admin key is ever sent in a
        path. The player-facing key endpoints (/drm/signed, /drm/keyfile) live on the AES
        gateway and are documented on the Scrambler page.
      </p>
    </div>
  );
}

export default AesSettings;
